// Parses the ```ui fence the StoreFront chat model emits (same contract as the
// iOS UIActionParser) into typed actions the web UI can apply.

export type UIAction =
  | { type: 'show_category'; category: string }
  | { type: 'show_item'; category: string; item: string }
  | { type: 'add_to_cart'; item: string; price: string | null; quantity: number; notes: string | null }
  | { type: 'remove_from_cart'; item: string }
  | { type: 'set_quantity'; item: string; quantity: number }
  | { type: 'show_cart' }
  | { type: 'clear_cart' }
  | { type: 'suggest_items'; items: { item: string; price: string | null }[] }
  | { type: 'show_hours' }
  | { type: 'show_about' };

interface RawAction {
  type?: unknown; category?: unknown; item?: unknown; price?: unknown;
  quantity?: unknown; notes?: unknown; items?: unknown;
}

const FENCE = /```ui\s*\n([\s\S]*?)\n```/;

export function parseUIActions(text: string): { cleanedText: string; actions: UIAction[] } {
  const m = FENCE.exec(text);
  if (!m) return { cleanedText: text, actions: [] };
  const cleanedText = text.replace(m[0], '').trim();
  let decoded: { actions?: unknown } | null = null;
  try { decoded = JSON.parse(m[1]) as { actions?: unknown }; } catch { return { cleanedText, actions: [] }; }
  const raw = Array.isArray(decoded?.actions) ? (decoded!.actions as RawAction[]) : [];
  const actions: UIAction[] = [];
  for (const a of raw) {
    const act = parseOne(a);
    if (act) actions.push(act);
  }
  return { cleanedText, actions };
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const int = (v: unknown, dflt: number): number => (Number.isInteger(v) && (v as number) > 0 ? (v as number) : dflt);

function parseOne(a: RawAction): UIAction | null {
  if (!a || typeof a !== 'object') return null;
  switch (a.type) {
    case 'show_category': { const c = str(a.category); return c ? { type: 'show_category', category: c } : null; }
    case 'show_item': { const c = str(a.category), i = str(a.item); return c && i ? { type: 'show_item', category: c, item: i } : null; }
    case 'add_to_cart': { const i = str(a.item); return i ? { type: 'add_to_cart', item: i, price: str(a.price), quantity: int(a.quantity, 1), notes: str(a.notes) } : null; }
    case 'remove_from_cart': { const i = str(a.item); return i ? { type: 'remove_from_cart', item: i } : null; }
    case 'set_quantity': { const i = str(a.item); return i && Number.isInteger(a.quantity) ? { type: 'set_quantity', item: i, quantity: a.quantity as number } : null; }
    case 'show_cart': return { type: 'show_cart' };
    case 'clear_cart': return { type: 'clear_cart' };
    case 'show_hours': return { type: 'show_hours' };
    case 'show_about': return { type: 'show_about' };
    case 'suggest_items': {
      const items = Array.isArray(a.items) ? (a.items as RawAction[]).map((x) => ({ item: str(x?.item), price: str(x?.price) })).filter((x): x is { item: string; price: string | null } => !!x.item) : [];
      return items.length ? { type: 'suggest_items', items } : null;
    }
    default: return null;
  }
}
